import { useCallback, useEffect, useRef, useState } from 'react';
import StatusToast from './StatusToast';
import { adminApi } from './api';
import QuestionEditor from './QuestionEditor';
import BulkQuestionImport from './BulkQuestionImport';
import BulkCatalogImport from './BulkCatalogImport';
import useAdminConfirm from './useAdminConfirm';

const ACCESS = [['free', 'Free'], ['premium', 'Premium']];
const STATUSES = { draft: 'Brouillon', published: 'Publié', archived: 'Archivé' };
const TYPES = [['direct', 'Concours direct'], ['professional', 'Concours professionnel']];
const EMPTY_LOT = { competition_type: 'direct', titre: '', description: '', display_order: 1, access_tier: 'free' };
const EMPTY_SUBJECT = { lot_id: '', titre: '', description: '', duree_minutes: 60, access_tier: 'free', display_order: 1 };
const EMPTY_FILTERS = { search: '', competition_type: '', lot_id: '', publication_status: '', access_tier: '', questions: '' };
const PAGE_SIZE = 30;
function Label({ children, ...props }) {
  return <label className="bo-sim-field" {...props}>{children}</label>;
}
function Status({ value }) {
  return <span className={`bo-badge bo-badge--${value || 'draft'}`}>{STATUSES[value] || value || '—'}</span>;
}
function SelectOptions({ options }) {
  return options.map(([value, name]) => <option key={value} value={value}>{name}</option>);
}
function cleanNumber(value, fallback = 0) {
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}
export default function SimulationManager({ refresh, onUnauthorized }) {
  const [tab, setTab] = useState('subjects');
  const [view, setView] = useState('catalog');
  const [createPanel, setCreatePanel] = useState(null);
  const [newLot, setNewLot] = useState(EMPTY_LOT);
  const [newSubject, setNewSubject] = useState(EMPTY_SUBJECT);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ sort: 'updated_at', direction: 'desc' });
  const [page, setPage] = useState(0);
  const [list, setList] = useState({ items: [], total: 0 });
  const [lotOptions, setLotOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const { askConfirm, confirmDialog } = useAdminConfirm();
  const [subject, setSubject] = useState(null);
  const [subjectEditor, setSubjectEditor] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [lot, setLot] = useState(null);
  const [lotEditor, setLotEditor] = useState(null);
  const [lotAccessTier, setLotAccessTier] = useState('free');
  const [subjectAccessTier, setSubjectAccessTier] = useState('free');
  const [questionOpen, setQuestionOpen] = useState(false);
  const [detailTab, setDetailTab] = useState('questions');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionFormVersion, setQuestionFormVersion] = useState(0);
  const [preview, setPreview] = useState(false);
  const [previewAnswers, setPreviewAnswers] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [reordering, setReordering] = useState(false);
  const dragging = useRef(null);
  const topRef = useRef(null);
  const questionFormRef = useRef(null);

  const onError = useCallback((err) => {
    if (err?.status === 401) onUnauthorized?.();
    else setError(err?.message || 'Opération impossible.');
  }, [onUnauthorized]);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      const query = {
        ...applied, limit: PAGE_SIZE, offset: page * PAGE_SIZE,
        ...(tab === 'subjects' ? sort : {}),
      };
      // Lots have an access default, but no subject/question-specific filters.
      if (tab === 'lots') {
        delete query.lot_id; delete query.questions;
        delete query.sort; delete query.direction;
      }
      const response = await adminApi.simulationCatalog(tab, query);
      setList(response);
    } catch (err) {
      onError(err);
      setList({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [tab, applied, sort, page, onError]);

  const loadOptions = useCallback(async () => {
    try { setLotOptions(await adminApi.simulationLotOptions()); }
    catch (err) { onError(err); }
  }, [onError]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadOptions(); }, [loadOptions]);
  useEffect(() => {
    if (view !== 'catalog') topRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [view, subject?.id, lot?.id]);
  useEffect(() => {
    if (view === 'subject' && questionOpen && !preview && detailTab === 'questions') {
      questionFormRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    }
  }, [view, questionOpen, editingQuestion, preview, detailTab]);

  async function refreshData() {
    await Promise.all([loadList(), loadOptions(), refresh?.()]);
  }
  async function loadSubject(id) {
    const [item, bundle] = await Promise.all([
      adminApi.simulationSubjectDetail(id), adminApi.simulationQuestions(id),
    ]);
    setSubject(item);
    setSubjectAccessTier(item.access_tier || 'free');
    setSubjectEditor({
      titre: item.titre, description: item.description || '',
      lot_id: String(item.lot_id || ''),
      duree_minutes: item.duree_minutes || '', display_order: item.display_order || 0,
    });
    setQuestions(bundle.questions || []);
    return item;
  }
  async function openSubject(id, message = '') {
    setError(''); setNotice(message);
    setSubject(null); setQuestions([]); setPreview(false); setQuestionOpen(false);
    setEditingQuestion(null); setPreviewIndex(0); setPreviewAnswers(false);
    setDetailTab('questions');
    setView('subject');
    try { await loadSubject(id); } catch (err) { onError(err); }
  }
  async function loadLot(id) {
    const item = await adminApi.simulationLotDetail(id);
    setLot(item);
    setLotAccessTier(item.access_tier || 'free');
    setLotEditor({
      titre: item.titre, description: item.description || '',
      competition_type: item.competition_type, display_order: item.display_order,
    });
    return item;
  }
  async function openLot(id, message = '') {
    setError(''); setNotice(message); setLot(null); setView('lot');
    try { await loadLot(id); } catch (err) { onError(err); }
  }
  function toCatalog() {
    setView('catalog'); setPreview(false); setQuestionOpen(false);
    setNotice(''); setError(''); loadList();
  }
  async function mutation(action, success, after = async () => {}) {
    if (busy) return false;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await action();
      await after(result);
      // A successful HTTP response alone does not prove the updated content
      // is visible. Announce success only after post-write verification.
      setNotice(success);
      return true;
    } catch (err) {
      onError(err);
      return false;
    } finally {
      setBusy(false);
    }
  }
  function chooseTab(value) {
    setTab(value); setPage(0); setApplied(EMPTY_FILTERS); setFilters(EMPTY_FILTERS);
    setError(''); setNotice('');
  }
  function createSubjectIn(lotId) {
    const selectedLot = lotOptions.find((row) => Number(row.id) === Number(lotId));
    setNewSubject({ ...EMPTY_SUBJECT, lot_id: String(lotId || ''),
      access_tier: selectedLot?.access_tier || lot?.access_tier || 'free' });
    setCreatePanel('subject');
    setView('catalog'); setTab('subjects');
    setError(''); setNotice('');
    topRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }

  async function submitLot(event) {
    event.preventDefault();
    await mutation(
      () => adminApi.createSimulationLot({
        ...newLot, display_order: cleanNumber(newLot.display_order, 1), publication_status: 'draft',
      }),
      'Lot créé en brouillon. Vous pouvez maintenant créer un sujet dans ce lot.',
      async (created) => {
        setNewLot(EMPTY_LOT); setCreatePanel(null);
        await refreshData();
        await openLot(created.id, 'Lot créé en brouillon. Créez maintenant un sujet dans ce lot.');
      }
    );
  }
  async function submitSubject(event) {
    event.preventDefault();
    await mutation(
      () => adminApi.createSimulation({
        ...newSubject, lot_id: Number(newSubject.lot_id),
        duree_minutes: Number(newSubject.duree_minutes),
        display_order: cleanNumber(newSubject.display_order),
        publication_status: 'draft',
      }),
      'Sujet créé en brouillon. Ajoutez au moins une question valide avant publication.',
      async (created) => {
        setNewSubject(EMPTY_SUBJECT); setCreatePanel(null);
        await refreshData();
        await openSubject(created.id, 'Sujet créé en brouillon. Ajoutez au moins une question valide avant publication.');
        // Show the next action without leaving the subject details.
        setQuestionOpen(true);
      }
    );
  }
  async function saveSubject(event) {
    event.preventDefault();
    if (!subject || subject.publication_status !== 'draft') return;
    await mutation(
      () => adminApi.updateSimulation(subject.id, {
        ...subjectEditor,
        lot_id: Number(subjectEditor.lot_id),
        duree_minutes: Number(subjectEditor.duree_minutes),
        display_order: cleanNumber(subjectEditor.display_order),
      }),
      'Informations du sujet enregistrées.',
      async () => { await loadSubject(subject.id); await refreshData(); }
    );
  }
  async function saveLot(event) {
    event.preventDefault();
    if (!lot || lot.publication_status !== 'draft') return;
    await mutation(
      () => adminApi.updateSimulationLot(lot.id, {
        ...lotEditor, display_order: cleanNumber(lotEditor.display_order),
      }),
      'Informations du lot enregistrées.',
      async () => { await loadLot(lot.id); await refreshData(); }
    );
  }
  async function updateSubjectAccess(event) {
    event.preventDefault();
    if (!subject || subjectAccessTier === subject.access_tier) return;
    await mutation(
      async () => {
        const updated = await adminApi.updateSimulation(subject.id, { access_tier: subjectAccessTier });
        if (updated?.access_tier !== subjectAccessTier) {
          throw new Error('L’API n’a pas confirmé le nouvel accès du sujet. Vérifiez le serveur.');
        }
        return updated;
      },
      'Accès du sujet confirmé : changement enregistré et relu depuis PostgreSQL.',
      async () => {
        const persisted = await loadSubject(subject.id);
        if (persisted.access_tier !== subjectAccessTier) {
          throw new Error(
            'Modification non confirmée : le sujet affiche toujours son ancien accès après relecture du serveur.'
          );
        }
        await refreshData();
      }
    );
  }
  async function updateLotAccess(event) {
    event.preventDefault();
    const allSubjectsMatch = lotAccessTier === 'premium'
      ? Number(lot.premium_subject_count || 0) === Number(lot.subject_count || 0)
      : Number(lot.premium_subject_count || 0) === 0;
    if (!lot || (lotAccessTier === lot.access_tier && allSubjectsMatch)) return;
    if (!(await askConfirm({
      title: 'Modifier l’accès de tout le lot ?',
      description: `Les ${lot.subject_count || 0} sujet(s) passeront en ${lotAccessTier === 'premium' ? 'Premium' : 'Free'}. Leurs accès individuels seront remplacés, y compris pour les sujets publiés.`,
      confirmLabel: 'Appliquer au lot',
      intent: 'danger',
    }))) return;
    await mutation(
      async () => {
        const updated = await adminApi.updateSimulationLot(lot.id, { access_tier: lotAccessTier });
        if (updated?.access_tier !== lotAccessTier) {
          throw new Error('L’API n’a pas confirmé le nouvel accès du lot. Vérifiez le serveur.');
        }
        if (updated.subject_count === undefined || updated.premium_subject_count === undefined) {
          throw new Error(
            'L’API de modification du lot ne renvoie pas les compteurs de vérification. '
            + 'Redémarrez l’API avec la dernière version.'
          );
        }
        const expectedPremium = lotAccessTier === 'premium' ? Number(updated.subject_count) : 0;
        if (Number(updated.premium_subject_count) !== expectedPremium) {
          throw new Error('L’API indique que certains sujets n’ont pas reçu le nouvel accès.');
        }
        return updated;
      },
      'Accès de tous les sujets du lot confirmé par PostgreSQL.',
      async () => {
        const persisted = await loadLot(lot.id);
        const expectedPremium = lotAccessTier === 'premium'
          ? Number(persisted.subject_count) : 0;
        if (persisted.access_tier !== lotAccessTier ||
            Number(persisted.premium_subject_count) !== expectedPremium) {
          throw new Error(
            'La relecture du serveur ne confirme pas la modification du lot. '
            + 'Vérifiez que l’API et les migrations utilisent la même base PostgreSQL.'
          );
        }
        await refreshData();
      }
    );
  }

  async function statusSubject(status) {
    if (!subject) return;
    if (status === 'archived' && !(await askConfirm({
      title: 'Archiver ce sujet ?',
      description: 'Son contenu sera conservé et ne sera plus présenté aux candidats.',
      confirmLabel: 'Archiver le sujet',
      intent: 'danger',
    }))) return;
    await mutation(
      () => adminApi.updateSimulation(subject.id, { publication_status: status }),
      status === 'published' ? 'Sujet publié. Vous pouvez maintenant publier son lot parent.' :
      status === 'draft' ? 'Sujet repassé en brouillon pour édition.' : 'Sujet archivé.',
      async () => { await loadSubject(subject.id); await refreshData(); }
    );
  }
  async function statusLot(status, item = lot) {
    if (!item) return;
    if (status === 'archived' && !(await askConfirm({
      title: 'Archiver ce lot ?',
      description: 'Les sujets sont conservés. Le lot ne sera plus visible dans les parcours candidats.',
      confirmLabel: 'Archiver le lot',
      intent: 'danger',
    }))) return;
    await mutation(
      () => adminApi.updateSimulationLot(item.id, { publication_status: status }),
      status === 'published' ? 'Lot publié.' : status === 'draft' ? 'Lot repassé en brouillon.' : 'Lot archivé.',
      async () => { if (lot?.id === item.id) await loadLot(item.id); await refreshData(); }
    );
  }
  async function saveQuestion(payload) {
    if (!subject || subject.publication_status !== 'draft') return;
    await mutation(
      () => editingQuestion ? adminApi.updateQuestion(editingQuestion.id, payload) :
        adminApi.createSimulationQuestion(subject.id, payload),
      editingQuestion ? 'Question mise à jour.' : 'Question ajoutée au sujet.',
      async () => {
        await loadSubject(subject.id);
        await refreshData();
        if (editingQuestion) {
          setEditingQuestion(null);
          setQuestionOpen(false);
        } else {
          // A freshly added question should leave a blank editor ready for
          // the next question; no repeated navigation to the page bottom.
          setQuestionFormVersion((v) => v + 1);
          setQuestionOpen(true);
        }
      }
    );
  }
  async function removeQuestion(q) {
    if (!subject || !(await askConfirm({
      title: 'Retirer cette question ?',
      description: 'Elle ne sera plus présentée aux candidats dans ce sujet.',
      confirmLabel: 'Retirer la question',
      intent: 'danger',
    }))) return;
    await mutation(
      () => adminApi.detachSimulationQuestion(subject.id, q.id),
      'Question retirée du sujet.',
      async () => { await loadSubject(subject.id); await refreshData(); }
    );
  }
  async function moveQuestion(id, toIndex) {
    if (reordering || busy || !subject || subject.publication_status !== 'draft') return;
    const from = questions.findIndex((q) => q.id === id);
    if (from < 0 || toIndex < 0 || toIndex >= questions.length || from === toIndex) return;
    const ids = questions.map((q) => q.id);
    ids.splice(from, 1); ids.splice(toIndex, 0, id);
    setReordering(true); setError('');
    try {
      await adminApi.reorderSimulationQuestions(subject.id, ids);
      setNotice('Ordre des questions enregistré.');
      await loadSubject(subject.id);
    } catch (err) { onError(err); }
    finally { setReordering(false); dragging.current = null; }
  }
  function sortBy(field) {
    setPage(0);
    setSort((old) => old.sort === field
      ? { sort: field, direction: old.direction === 'asc' ? 'desc' : 'asc' }
      : { sort: field, direction: 'asc' });
  }
  const activeOptions = lotOptions.filter((row) => row.publication_status !== 'archived');
  const draftSubject = subject?.publication_status === 'draft';
  const draftLot = lot?.publication_status === 'draft';
  const selectedQuestion = questions[Math.min(previewIndex, Math.max(questions.length - 1, 0))];
  return (
    <div className="bo-stack bo-sim-workspace" ref={topRef}>
      {confirmDialog}
      <header className="bo-panel__header bo-sim-page-head">
        <div>
          <p className="bo-eyebrow">Contenus</p>
          <h2>Simulations</h2>
          <p className="bo-muted">Type de concours → lot → sujet → questions.</p>
        </div>
        {view !== 'catalog' && (
          <button className="bo-button bo-button--ghost" onClick={toCatalog}>
            Retour au catalogue
          </button>
        )}
      </header>
      {error && <div className="bo-alert bo-alert--error" role="alert">{error}</div>}
      <StatusToast message={notice} />

      {view === 'catalog' && <BulkCatalogImport kind={tab === 'lots' ? 'simulation_lots' : 'simulations'} onImported={refreshData} />}
      {view === 'catalog' && <>
        <div className="bo-panel bo-sim-catalog">
          <div className="bo-panel__header">
            <div className="bo-actions">
              <button className={`bo-button ${tab === 'subjects' ? 'bo-button--primary' : 'bo-button--ghost'}`}
                onClick={() => chooseTab('subjects')}>Sujets</button>
              <button className={`bo-button ${tab === 'lots' ? 'bo-button--primary' : 'bo-button--ghost'}`}
                onClick={() => chooseTab('lots')}>Lots</button>
            </div>
            <div className="bo-actions">
              <button className="bo-button bo-button--ghost"
                onClick={() => setCreatePanel((old) => old === 'lot' ? null : 'lot')}>+ Nouveau lot</button>
              <button className="bo-button bo-button--primary"
                onClick={() => setCreatePanel((old) => old === 'subject' ? null : 'subject')}>+ Nouveau sujet</button>
            </div>
          </div>
          {createPanel === 'lot' && <form className="bo-sim-inline-create" onSubmit={submitLot}>
            <h3>Créer un lot</h3>
            <div className="bo-sim-form-grid">
              <Label>Type de concours
                <select className="bo-input" value={newLot.competition_type}
                  onChange={(e) => setNewLot((p) => ({ ...p, competition_type: e.target.value }))}>
                  <SelectOptions options={TYPES}/>
                </select>
              </Label>
              <Label>Nom du lot
                <input className="bo-input" required maxLength="255" value={newLot.titre}
                  placeholder="Ex. Lot 1"
                  onChange={(e) => setNewLot((p) => ({ ...p, titre: e.target.value }))}/>
              </Label>
              <Label>Accès par défaut des sujets du lot
                <select className="bo-input" value={newLot.access_tier}
                  onChange={(e) => setNewLot((p) => ({ ...p, access_tier: e.target.value }))}>
                  <SelectOptions options={ACCESS}/>
                </select>
              </Label>
              <Label>Description (facultative)
                <textarea className="bo-input" value={newLot.description}
                  onChange={(e) => setNewLot((p) => ({ ...p, description: e.target.value }))}/>
              </Label>
              <Label>Ordre d'affichage
                <input className="bo-input" type="number" min="0" value={newLot.display_order}
                  onChange={(e) => setNewLot((p) => ({ ...p, display_order: e.target.value }))}/>
              </Label>
            </div>
            <div className="bo-actions">
              <button className="bo-button bo-button--primary" disabled={busy}>Créer le lot en brouillon</button>
              <button type="button" className="bo-button bo-button--ghost"
                onClick={() => setCreatePanel(null)}>Annuler</button>
            </div>
          </form>}
          {createPanel === 'subject' && <form className="bo-sim-inline-create" onSubmit={submitSubject}>
            <h3>Créer un sujet</h3>
            <p className="bo-muted">Après création, l'éditeur du sujet s'ouvrira automatiquement pour ajouter les questions.</p>
            <div className="bo-sim-form-grid">
              <Label>Lot obligatoire
                <select className="bo-input" required value={newSubject.lot_id}
                  onChange={(e) => {
                    const selectedLot = lotOptions.find((item) => String(item.id) === e.target.value);
                    setNewSubject((p) => ({ ...p, lot_id: e.target.value,
                      access_tier: selectedLot?.access_tier || 'free' }));
                  }}>
                  <option value="">Choisir un lot…</option>
                  {activeOptions.map((item) => <option key={item.id} value={item.id}>
                    {item.competition_type === 'professional' ? 'Professionnel' : 'Direct'} · {item.titre}
                  </option>)}
                </select>
              </Label>
              <Label>Titre du sujet
                <input className="bo-input" required maxLength="255" value={newSubject.titre}
                  onChange={(e) => setNewSubject((p) => ({ ...p, titre: e.target.value }))}/>
              </Label>
              <Label>Durée en minutes
                <input className="bo-input" required type="number" min="1" value={newSubject.duree_minutes}
                  onChange={(e) => setNewSubject((p) => ({ ...p, duree_minutes: e.target.value }))}/>
              </Label>
              <Label>Accès du nouveau sujet
                <select className="bo-input" value={newSubject.access_tier}
                  onChange={(e) => setNewSubject((p) => ({ ...p, access_tier: e.target.value }))}>
                  <SelectOptions options={ACCESS}/>
                </select>
              </Label>
              <Label>Description (facultative)
                <textarea className="bo-input" value={newSubject.description}
                  onChange={(e) => setNewSubject((p) => ({ ...p, description: e.target.value }))}/>
              </Label>
            </div>
            <div className="bo-actions">
              <button className="bo-button bo-button--primary" disabled={busy}>Créer et ajouter les questions</button>
              <button type="button" className="bo-button bo-button--ghost"
                onClick={() => setCreatePanel(null)}>Annuler</button>
            </div>
          </form>}
          <form className="bo-sim-filters" onSubmit={(e) => {
            e.preventDefault(); setApplied({ ...filters }); setPage(0);
          }}>
            <Label>Rechercher
              <input className="bo-input" placeholder="Titre, description, lot…" value={filters.search}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}/>
            </Label>
            <Label>Type de concours
              <select className="bo-input" value={filters.competition_type}
                onChange={(e) => setFilters((p) => ({ ...p, competition_type: e.target.value }))}>
                <option value="">Tous les types</option><SelectOptions options={TYPES}/>
              </select>
            </Label>
            {tab === 'subjects' && <Label>Lot
              <select className="bo-input" value={filters.lot_id}
                onChange={(e) => setFilters((p) => ({ ...p, lot_id: e.target.value }))}>
                <option value="">Tous les lots</option>
                {lotOptions.map((item) => <option key={item.id} value={item.id}>{item.titre}</option>)}
              </select>
            </Label>}
            <Label>Publication
              <select className="bo-input" value={filters.publication_status}
                onChange={(e) => setFilters((p) => ({ ...p, publication_status: e.target.value }))}>
                <option value="">Tous les états</option>
                {Object.entries(STATUSES).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </Label>
            <Label>Accès
                <select className="bo-input" value={filters.access_tier}
                  onChange={(e) => setFilters((p) => ({ ...p, access_tier: e.target.value }))}>
                  <option value="">Tous</option><SelectOptions options={ACCESS}/>
                </select>
              </Label>
              {tab === 'subjects' && <Label>Questions
                <select className="bo-input" value={filters.questions}
                  onChange={(e) => setFilters((p) => ({ ...p, questions: e.target.value }))}>
                  <option value="">Tous</option>
                  <option value="empty">Sans question</option>
                  <option value="nonempty">Avec questions</option>
                </select>
              </Label>}
            <div className="bo-actions">
              <button className="bo-button bo-button--primary" disabled={loading}>Rechercher</button>
              <button type="button" className="bo-button bo-button--ghost" onClick={() => {
                setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(0);
              }}>Réinitialiser</button>
            </div>
          </form>
          {loading ? <div className="bo-loading">Chargement du catalogue…</div> : (
            <div className="bo-table-wrap bo-sim-table-wrap">
              <table className="bo-table bo-sim-table">
                <thead><tr>
                  {tab === 'subjects' ? <>
                    {[
                      ['title', 'Sujet'], ['lot', 'Lot'], ['questions', 'Questions'],
                      ['duration', 'Durée'], ['access', 'Accès'], ['status', 'État'],
                      ['updated_at', 'Mise à jour'],
                    ].map(([key, label]) => <th key={key}>
                      <button className="bo-sim-sort" onClick={() => sortBy(key)} aria-label={`Trier par ${label}`}>
                        {label} {sort.sort === key ? (sort.direction === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>)}
                    <th>Action</th>
                  </> : <>
                    <th>Lot</th><th>Type</th><th>Sujets</th><th>Sujets publiés</th>
                    <th>Accès du lot</th><th>État</th><th>Action</th>
                  </>}
                </tr></thead>
                <tbody>
                  {(list.items || []).map((item) => tab === 'subjects' ? (
                    <tr key={item.id} onClick={() => openSubject(item.id)}>
                      <td><strong>{item.titre}</strong></td>
                      <td>{item.lot_title || 'Non classé'}</td>
                      <td>{item.question_count}</td>
                      <td>{item.duree_minutes ?? '—'} min</td>
                      <td>{item.access_tier === 'premium' ? 'Premium' : 'Free'}</td>
                      <td><Status value={item.publication_status}/></td>
                      <td>{item.updated_at ? new Date(item.updated_at).toLocaleDateString('fr-FR') : '—'}</td>
                      <td><button className="bo-button bo-button--ghost bo-button--compact"
                        onClick={(e) => { e.stopPropagation(); openSubject(item.id); }}>Ouvrir</button></td>
                    </tr>
                  ) : (
                    <tr key={item.id} onClick={() => openLot(item.id)}>
                      <td><strong>{item.titre}</strong></td>
                      <td>{item.competition_type === 'professional' ? 'Professionnel' : 'Direct'}</td>
                      <td>{item.subject_count}</td>
                      <td>{item.published_subject_count}</td>
                      <td>{item.access_tier === 'premium' ? 'Premium' : 'Free'}
                        {Number(item.subject_count || 0) > 0 &&
                          Number(item.premium_subject_count || 0) > 0 &&
                          Number(item.premium_subject_count || 0) < Number(item.subject_count) &&
                          <small className="bo-muted"> · Sujets mixtes</small>}
                      </td>
                      <td><Status value={item.publication_status}/></td>
                      <td><button className="bo-button bo-button--ghost bo-button--compact"
                        onClick={(e) => { e.stopPropagation(); openLot(item.id); }}>Ouvrir</button></td>
                    </tr>
                  ))}
                  {!list.items?.length && <tr><td colSpan={tab === 'subjects' ? 8 : 7}>Aucun résultat.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          <div className="bo-panel__header bo-sim-pagination">
            <p className="bo-muted">{list.total || 0} résultat(s) · Page {page + 1} / {Math.max(1, Math.ceil((list.total || 0) / PAGE_SIZE))}</p>
            <div className="bo-actions">
              <button className="bo-button bo-button--ghost" disabled={page <= 0 || loading}
                onClick={() => setPage((v) => Math.max(0, v - 1))}>Précédent</button>
              <button className="bo-button bo-button--ghost" disabled={loading || (page + 1) * PAGE_SIZE >= list.total}
                onClick={() => setPage((v) => v + 1)}>Suivant</button>
              <button type="button" className="bo-icon-action" onClick={refreshData}
                  aria-label="Actualiser le catalogue des simulations" title="Actualiser le catalogue des simulations">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 11a8 8 0 0 0-14.9-4M4 4v4h4M4 13a8 8 0 0 0 14.9 4M20 20v-4h-4" />
                  </svg>
                </button>
            </div>
          </div>
        </div>
      </>}

      {view === 'lot' && (lot && lotEditor ? (
        <section className="bo-panel bo-sim-detail">
          <div className="bo-panel__header">
            <div><h3>{lot.titre}</h3><p className="bo-muted">Lot · {lot.competition_type === 'professional' ? 'Concours professionnel' : 'Concours direct'}</p></div>
            <Status value={lot.publication_status}/>
          </div>
          <div className="bo-sim-summary">
            <span>{lot.subject_count} sujet(s)</span>
            <span>{lot.published_subject_count} sujet(s) publié(s)</span>
            <span>{lot.premium_subject_count || 0} sujet(s) Premium</span>
            <span>{lot.publication_status === 'published' ? 'Lot visible côté candidat sous réserve des accès.' : 'Lot non publié.'}</span>
          </div>
          <div className="bo-actions bo-sim-main-actions">
            <button className="bo-button bo-button--primary" onClick={() => createSubjectIn(lot.id)}>
              + Créer un sujet dans ce lot
            </button>
            {lot.publication_status === 'draft' && <>
              <button className="bo-button bo-button--secondary" disabled={busy || !lot.published_subject_count}
                onClick={() => statusLot('published')}>Publier le lot</button>
              <button className="bo-button bo-button--danger-ghost" disabled={busy}
                onClick={() => statusLot('archived')}>Archiver</button>
            </>}
            {lot.publication_status === 'published' && <button className="bo-button bo-button--ghost" disabled={busy}
              onClick={() => statusLot('draft')}>Repasser en brouillon</button>}
            {lot.publication_status === 'archived' && <button className="bo-button bo-button--ghost" disabled={busy}
              onClick={() => statusLot('draft')}>Restaurer en brouillon</button>}
          </div>
          {!lot.published_subject_count && lot.publication_status === 'draft' && (
            <p className="bo-muted">Pour publier ce lot, publiez d'abord au moins un sujet contenant une question valide.</p>
          )}
          <form className="bo-sim-access-form" onSubmit={updateLotAccess}>
            <h4>Accès Free / Premium du lot complet</h4>
            <p className="bo-muted">
              Appliquer ce choix à tous les sujets du lot, publiés ou non. Les nouveaux sujets
              proposeront également ce niveau par défaut. Un sujet pourra ensuite être
              modifié individuellement, rendant les accès du lot mixtes.
            </p>
            <div className="bo-sim-access-fields">
              <Label>Accès à appliquer à tout le lot
                <select className="bo-input" value={lotAccessTier}
                  onChange={(e) => setLotAccessTier(e.target.value)}>
                  <SelectOptions options={ACCESS}/>
                </select>
              </Label>
              <button type="submit" className="bo-button bo-button--primary"
                disabled={busy || (lotAccessTier === lot.access_tier &&
                  (lotAccessTier === 'premium'
                    ? Number(lot.premium_subject_count || 0) === Number(lot.subject_count || 0)
                    : Number(lot.premium_subject_count || 0) === 0))}>
                Appliquer à tous les sujets du lot
              </button>
            </div>
          </form>
          <form className="bo-sim-meta-form" onSubmit={saveLot}>
            <h4>Informations du lot</h4>
            <div className="bo-sim-form-grid">
              <Label>Titre
                <input className="bo-input" required disabled={!draftLot} value={lotEditor.titre}
                  onChange={(e) => setLotEditor((p) => ({ ...p, titre: e.target.value }))}/>
              </Label>
              <Label>Type de concours
                <select className="bo-input" disabled={!draftLot} value={lotEditor.competition_type}
                  onChange={(e) => setLotEditor((p) => ({ ...p, competition_type: e.target.value }))}>
                  <SelectOptions options={TYPES}/>
                </select>
              </Label>
              <Label>Description
                <textarea className="bo-input" disabled={!draftLot} value={lotEditor.description}
                  onChange={(e) => setLotEditor((p) => ({ ...p, description: e.target.value }))}/>
              </Label>
              <Label>Ordre d'affichage
                <input className="bo-input" disabled={!draftLot} type="number" min="0" value={lotEditor.display_order}
                  onChange={(e) => setLotEditor((p) => ({ ...p, display_order: e.target.value }))}/>
              </Label>
            </div>
            {draftLot && <button className="bo-button bo-button--primary" disabled={busy}>Enregistrer les informations</button>}
          </form>
        </section>
      ) : <div className="bo-loading">Chargement du lot…</div>)}

      {view === 'subject' && (subject && subjectEditor ? (
        <section className="bo-panel bo-sim-detail">
          <div className="bo-panel__header">
            <div><h3>{subject.titre}</h3><p className="bo-muted">{subject.lot_title || 'Lot non classé'} · {subject.duree_minutes ?? '—'} min · {subject.access_tier === 'premium' ? 'Premium' : 'Free'}</p></div>
            <Status value={subject.publication_status}/>
          </div>
          <div className="bo-sim-summary">
            <span>1. Sujet créé ✓</span>
            <span>2. Questions valides : {subject.question_count || 0} {subject.question_count > 0 ? '✓' : '—'}</span>
            <span>3. {subject.publication_status === 'published' ? 'Sujet publié ✓' : 'Publication en attente'}</span>
            <span>4. Lot {subject.lot_publication_status === 'published' ? 'publié ✓' : 'non publié'}</span>
          </div>
          <div className="bo-actions bo-sim-main-actions">
            {draftSubject && <button className="bo-button bo-button--primary"
              onClick={() => { setPreview(false); setDetailTab('questions'); setQuestionOpen(true); setEditingQuestion(null); }}>
              + Ajouter une question
            </button>}
            <button className="bo-button bo-button--ghost" disabled={!questions.length}
              onClick={() => { setPreview((value) => !value); setQuestionOpen(false); setPreviewAnswers(false); setPreviewIndex(0); }}>
              {preview ? 'Retour à l’édition' : 'Prévisualiser le sujet'}
            </button>
            {draftSubject && <button className="bo-button bo-button--secondary" disabled={busy || !questions.length}
              onClick={() => statusSubject('published')}>Publier le sujet</button>}
            {subject.publication_status === 'published' && <button className="bo-button bo-button--ghost" disabled={busy}
              onClick={() => statusSubject('draft')}>Repasser en brouillon</button>}
            {subject.publication_status === 'draft' && <button className="bo-button bo-button--danger-ghost" disabled={busy}
              onClick={() => statusSubject('archived')}>Archiver le sujet</button>}
            {subject.publication_status === 'archived' && <button className="bo-button bo-button--ghost" disabled={busy}
              onClick={() => statusSubject('draft')}>Restaurer en brouillon</button>}
            {subject.publication_status === 'published' && subject.lot_id && subject.lot_publication_status !== 'published' && (
              <button className="bo-button bo-button--primary" onClick={() => openLot(subject.lot_id,
                'Le sujet est publié. Vérifiez puis publiez son lot.')}>Étape suivante : publier le lot</button>
            )}
          </div>
          {draftSubject && <BulkQuestionImport kind="simulation" subjectId={subject.id}
            onImported={async () => { await loadSubject(subject.id); await refreshData(); }} />}
          <form className="bo-sim-access-form" onSubmit={updateSubjectAccess}>
            <h4>Accès Free / Premium du sujet</h4>
            <p className="bo-muted">
              Le niveau d’accès peut être changé sans dépublier le sujet, même après
              publication. Les questions Premium restent protégées côté API.
            </p>
            <div className="bo-sim-access-fields">
              <Label>Accès du sujet
                <select className="bo-input" value={subjectAccessTier}
                  onChange={(e) => setSubjectAccessTier(e.target.value)}>
                  <SelectOptions options={ACCESS}/>
                </select>
              </Label>
              <button type="submit" className="bo-button bo-button--primary"
                disabled={busy || subjectAccessTier === subject.access_tier}>
                Enregistrer l’accès du sujet
              </button>
            </div>
          </form>
          {!questions.length && <div className="bo-sim-next-step">
            <strong>Ce sujet n'a encore aucune question.</strong>
            <p>Ajoutez au moins une question avec ses propositions et sa bonne réponse pour pouvoir le publier.</p>
          </div>}
          {!preview && <div className="bo-actions bo-sim-detail-tabs" role="group" aria-label="Sections du sujet">
            <button className={`bo-button ${detailTab === 'questions' ? 'bo-button--primary' : 'bo-button--ghost'}`}
              onClick={() => setDetailTab('questions')}>Questions ({questions.length})</button>
            <button className={`bo-button ${detailTab === 'information' ? 'bo-button--primary' : 'bo-button--ghost'}`}
              onClick={() => setDetailTab('information')}>Informations du sujet</button>
          </div>}
          {preview ? (
            <div className="bo-sim-preview">
              <h4>Aperçu éditorial de l'épreuve</h4>
              <p className="bo-muted">Prévisualisation pour contrôle avant publication (sans chronomètre réel ni tentative enregistrée).</p>
              <p><strong>{subject.titre}</strong> · {subject.duree_minutes || '—'} minutes · {questions.length} question(s)</p>
              {selectedQuestion && <>
                <h4>Question {previewIndex + 1} / {questions.length}</h4>
                <p>{selectedQuestion.question_text}</p>
                <div className="bo-sim-preview-choices">
                  {(selectedQuestion.choices || []).map((choice, index) => (
                    <div key={choice.id || index} className="bo-sim-preview-choice">
                      {String.fromCharCode(65 + index)}. {choice.choice_text ?? choice.text}
                      {previewAnswers && choice.is_correct && <strong> ✓ Bonne réponse</strong>}
                    </div>
                  ))}
                </div>
                {previewAnswers && selectedQuestion.explication && <p><strong>Explication :</strong> {selectedQuestion.explication}</p>}
                <div className="bo-actions">
                  <button className="bo-button bo-button--ghost" onClick={() => setPreviewIndex((i) => Math.max(0, i-1))}
                    disabled={previewIndex === 0}>Précédente</button>
                  <button className="bo-button bo-button--ghost" onClick={() => setPreviewAnswers((v) => !v)}>
                    {previewAnswers ? 'Masquer la correction' : 'Afficher la correction'}
                  </button>
                  <button className="bo-button bo-button--ghost" onClick={() => {
                    setPreviewIndex((i) => Math.min(questions.length-1, i+1)); setPreviewAnswers(false);
                  }} disabled={previewIndex >= questions.length-1}>Suivante</button>
                </div>
              </>}
            </div>
          ) : <>
            {detailTab === 'information' && (
            <form className="bo-sim-meta-form" onSubmit={saveSubject}>
              <h4>Informations du sujet</h4>
              <div className="bo-sim-form-grid">
                <Label>Titre
                  <input className="bo-input" required disabled={!draftSubject} value={subjectEditor.titre}
                    onChange={(e) => setSubjectEditor((p) => ({ ...p, titre: e.target.value }))}/>
                </Label>
                <Label>Lot de rattachement
                  <select className="bo-input" required disabled={!draftSubject} value={subjectEditor.lot_id}
                    onChange={(e) => setSubjectEditor((p) => ({ ...p, lot_id: e.target.value }))}>
                    <option value="">Choisir…</option>
                    {activeOptions.map((item) => <option value={item.id} key={item.id}>
                      {item.competition_type === 'professional' ? 'Professionnel' : 'Direct'} · {item.titre}
                    </option>)}
                  </select>
                </Label>
                <Label>Durée en minutes
                  <input className="bo-input" required disabled={!draftSubject} type="number" min="1"
                    value={subjectEditor.duree_minutes}
                    onChange={(e) => setSubjectEditor((p) => ({ ...p, duree_minutes: e.target.value }))}/>
                </Label>
                 <Label>Description
                  <textarea className="bo-input" disabled={!draftSubject} value={subjectEditor.description}
                    onChange={(e) => setSubjectEditor((p) => ({ ...p, description: e.target.value }))}/>
                </Label>
                <Label>Ordre d'affichage
                  <input className="bo-input" type="number" min="0" disabled={!draftSubject}
                    value={subjectEditor.display_order}
                    onChange={(e) => setSubjectEditor((p) => ({ ...p, display_order: e.target.value }))}/>
                </Label>
              </div>
              {draftSubject && <button className="bo-button bo-button--primary" disabled={busy}>Enregistrer les informations</button>}
            </form>
            )}
            {detailTab === 'questions' && <>
            {draftSubject && questionOpen && (
              <div className="bo-editor-block bo-sim-question-editor" ref={questionFormRef}>
                <div className="bo-panel__header">
                  <h4>{editingQuestion ? 'Modifier la question' : 'Ajouter une question'}</h4>
                  <button className="bo-button bo-button--ghost" onClick={() => {
                    setEditingQuestion(null); setQuestionOpen(false);
                  }}>Fermer l'éditeur</button>
                </div>
                <QuestionEditor
                  key={editingQuestion?.id || `new-${questionFormVersion}`}
                  initialQuestion={editingQuestion} onSave={saveQuestion} busy={busy}
                  onCancel={() => { setEditingQuestion(null); setQuestionOpen(false); }}
                />
              </div>
            )}
            <div className="bo-sim-questions">
              <div className="bo-panel__header">
                <h4>Questions ({questions.length})</h4>
                {!draftSubject && <p className="bo-muted">Repasser le sujet en brouillon pour modifier les questions.</p>}
              </div>
              {questions.map((question, index) => (
                <article className="bo-sim-question" key={question.id}
                  onDragOver={(e) => {
                    if (draftSubject && dragging.current !== null) e.preventDefault();
                  }}
                  onDrop={(e) => {
                    if (!draftSubject || dragging.current === null) return;
                    e.preventDefault(); moveQuestion(dragging.current, index);
                  }}>
                  <div className="bo-panel__header">
                    <div><strong>Q{index + 1}. {question.question_text}</strong>
                      <p className="bo-muted">{question.choices?.length || 0} proposition(s) · {question.difficulte || 'Niveau non renseigné'}</p>
                    </div>
                    {draftSubject && <div className="bo-actions">
                      <span className="bo-course-drag-handle" draggable={!busy && !reordering}
                        title="Glisser pour réordonner" aria-label={`Déplacer la question ${index + 1}`}
                        onDragStart={(e) => { dragging.current = question.id; e.dataTransfer.setData('text/plain', String(question.id)); }}
                        onDragEnd={() => { dragging.current = null; }}>↕</span>
                      <button className="bo-button bo-button--ghost bo-button--compact" disabled={reordering || !index}
                        onClick={() => moveQuestion(question.id, index - 1)} aria-label={`Monter la question ${index + 1}`}>↑</button>
                      <button className="bo-button bo-button--ghost bo-button--compact" disabled={reordering || index === questions.length - 1}
                        onClick={() => moveQuestion(question.id, index + 1)} aria-label={`Descendre la question ${index + 1}`}>↓</button>
                      <button className="bo-button bo-button--ghost bo-button--compact" onClick={() => {
                        setEditingQuestion(question); setQuestionOpen(true);
                      }}>Modifier</button>
                      <button className="bo-button bo-button--danger-ghost bo-button--compact"
                        onClick={() => removeQuestion(question)}>Retirer</button>
                    </div>}
                  </div>
                </article>
              ))}
            </div>

            </>}
          </>}
        </section>
      ) : <div className="bo-loading">Chargement du sujet…</div>)}
    </div>
  );
}
